import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../../api";
import Topbar from "../Topbar";
import useIsMobile from "../../hooks/useIsMobile";
import CallView, { Hl } from "./CallView";
import { VIEWS, PERIODS, termsOf, monthGroups, dayLabel, minutesLabel, theirNames } from "./callsModel.mjs";
import "./calls.css";

/*
 * MEETINGS ▸ CALLS. His Fireflies calls with leads, newest first, filed under
 * the company it was with (src/calls/log.js): not a client's calls while they
 * are a client (those live in the auditor), not internal ones, and only calls
 * he was on (2026-10-26). Click one and its notes and
 * transcript open beside the list. Asked for 2026-10-22 "similarly to what we
 * built for the auditor" (Pulse → Clients), with a way to open a call from the
 * lead's record (record/CallsGroup.js links here).
 *
 *   /meetings/calls               the log; on a wide screen the newest call opens
 *   /meetings/calls/:id           that call open beside the list (the record's link)
 *   ?view=unfiled (people the CRM doesn't know)   ?q=words   ?days=90   ?company=<id>
 *
 * The list stays put while a call is open (the Approvals split), and ↑/↓ move
 * through it. On a phone it is one pane at a time.
 */
function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function ago(iso) {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  return hours < 24 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`;
}

export default function CallsPage() {
  const { id: callId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const phone = useIsMobile();
  const view = VIEWS.some(([k]) => k === params.get("view")) ? params.get("view") : "leads";
  const days = Number(params.get("days")) || 0;
  const company = params.get("company") || null;
  const [q, setQ] = useState(params.get("q") || "");
  const dq = useDebounced(q, 250);
  const terms = useMemo(() => termsOf(dq), [dq]);
  const [list, setList] = useState({ status: "loading" });
  const [sync, setSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const listRef = useRef(null);

  const setParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );
  useEffect(() => {
    if ((params.get("q") || "") !== dq) setParam("q", dq);
  }, [dq]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (view !== "leads") p.set("view", view);
    if (dq.trim()) p.set("q", dq.trim());
    if (days) p.set("days", String(days));
    if (company) p.set("company_id", company);
    return p.toString();
  }, [view, dq, days, company]);

  useEffect(() => {
    let alive = true;
    setList((s) => ({ ...s, status: "loading" }));
    api(`/calls${query ? `?${query}` : ""}`)
      .then((d) => alive && setList({ status: "ok", ...d }))
      .catch((e) => alive && setList({ status: "error", error: e.message }));
    return () => {
      alive = false;
    };
  }, [query, reloadKey]);

  const loadStatus = useCallback(() => api("/calls/status").then(setSync).catch(() => null), []);
  useEffect(() => {
    loadStatus();
  }, [loadStatus]);
  // While a sync runs (the first one reads the whole history), check back, and
  // reload the list once it is done.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (!sync) return undefined;
    if (sync.running) {
      wasRunning.current = true;
      const t = setTimeout(loadStatus, 8000);
      return () => clearTimeout(t);
    }
    if (wasRunning.current) {
      wasRunning.current = false;
      setReloadKey((k) => k + 1);
    }
    return undefined;
  }, [sync, loadStatus]);

  const calls = useMemo(() => (list.calls || []), [list.calls]);
  // The search and tab ride along in the URL; read when a call opens, not when
  // the page drew, since typing replaces them in between.
  const open = useCallback(
    (id) => navigate(`/meetings/calls/${encodeURIComponent(id)}${window.location.search}`, { replace: !phone }),
    [navigate, phone]
  );

  // Beside the list, a call is always open: the newest, until he picks one, and
  // the newest match once a search or a tab leaves the open one out. A call
  // opened from a record's link stays open until he changes the list.
  const firstQuery = useRef(query);
  useEffect(() => {
    if (phone || list.status !== "ok" || !calls.length) return;
    if (!callId) open(calls[0].id);
    else if (query !== firstQuery.current && !calls.some((c) => c.id === callId)) open(calls[0].id);
  }, [phone, callId, list.status, calls, open, query]);

  useEffect(() => {
    const el = listRef.current && listRef.current.querySelector(".cl-row--on");
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [callId]);

  const onKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
    const i = calls.findIndex((c) => c.id === callId);
    const next = calls[Math.max(0, Math.min(calls.length - 1, i + (e.key === "ArrowDown" ? 1 : -1)))];
    if (next && next.id !== callId) {
      e.preventDefault();
      open(next.id);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api("/calls/sync", { method: "POST", body: "{}" });
    } catch (e) {
      // The status line below says what went wrong.
    } finally {
      setSyncing(false);
      loadStatus();
      setReloadKey((k) => k + 1);
    }
  };

  const counts = list.counts || {};
  const companyName = company && calls[0] && calls[0].company_id === company ? calls[0].company_name : null;
  const groups = monthGroups(calls);
  const synced = sync && sync.last_run_at ? `Synced ${ago(sync.last_run_at)}` : null;
  const never = sync && sync.enabled && !sync.backfilled_at;

  const toolbar = (
    <div className="cl-toolbar">
      <div className="cl-searchbox">
        <input
          className="cl-input cl-search" placeholder="Search calls: title, people, company, notes" value={q} spellCheck={false}
          onChange={(e) => setQ(e.target.value)} aria-label="Search calls"
        />
        {q && <button type="button" className="cl-clear" onClick={() => setQ("")} aria-label="Clear search">×</button>}
      </div>
      <div className="cl-toolbar__row">
        <div className="crm-seg crm-seg--scope cl-views" role="group" aria-label="Which calls">
          {VIEWS.map(([k, label]) => (
            <button key={k} type="button" className={view === k ? "sel" : ""} onClick={() => setParam("view", k === "leads" ? null : k)}>
              {label}{counts[k] !== undefined ? ` · ${counts[k]}` : ""}
            </button>
          ))}
        </div>
        <select className="cl-select" value={days} onChange={(e) => setParam("days", Number(e.target.value) ? e.target.value : null)} aria-label="Period">
          {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {company && (
        <div className="cl-filter">
          Calls with <b>{companyName || "this company"}</b>
          <button type="button" className="cl-link" onClick={() => setParam("company", null)}>Show every call</button>
        </div>
      )}
    </div>
  );

  const listPane = (
    <section className="cl-list" onKeyDown={onKey} tabIndex={-1} aria-label="Calls">
      {toolbar}
      <div className={`cl-list__body${list.status === "loading" ? " cl-busy" : ""}`} ref={listRef}>
        {list.status === "error" && <div className="cl-empty">Couldn't load the calls: {list.error}</div>}
        {list.status === "ok" && !calls.length && (
          never ? (
            <div className="cl-empty">The first sync reads every call Fireflies has. It takes a few minutes; this fills in when it's done.</div>
          ) : terms.length || days || view !== "leads" || company ? (
            <div className="cl-empty">Nothing matches. Try fewer words or a longer period.</div>
          ) : (
            <div className="cl-empty">No calls yet. A call shows up here within 15 minutes of Fireflies finishing its notes.</div>
          )
        )}
        {groups.map((g) => (
          <div key={g.label} className="cl-month">
            <div className="cl-month__label">{g.label}</div>
            {g.calls.map((c) => (
              <CallRow key={c.id} c={c} on={c.id === callId} terms={terms} onOpen={() => open(c.id)} />
            ))}
          </div>
        ))}
        {list.truncated && <div className="cl-empty cl-empty--small">Showing the newest {calls.length}. Search or narrow the period to see older ones.</div>}
      </div>
    </section>
  );

  const top = (
    <Topbar
      title="Calls"
      sub={`Your calls with leads${counts.leads !== undefined ? ` · ${counts.leads}` : ""}`}
      subMobile={counts.leads !== undefined ? `${counts.leads} calls` : null}
    >
      {sync && !sync.enabled ? (
        <span className="cl-muted">Fireflies isn't connected</span>
      ) : (
        <>
          {sync && sync.last_error && <span className="cl-err" title={sync.last_error}>Last sync failed</span>}
          {synced && !(sync && sync.last_error) && <span className="cl-muted cl-synced">{synced}</span>}
          <button type="button" className="crm-btn crm-btn--secondary cl-syncbtn" onClick={syncNow} disabled={syncing || (sync && sync.running)}>
            {syncing || (sync && sync.running) ? "Syncing…" : "Sync now"}
          </button>
        </>
      )}
    </Topbar>
  );

  if (phone) {
    if (callId) {
      return (
        <>
          {top}
          <div className="cl-phone">
            <button type="button" className="cl-back" onClick={() => navigate(`/meetings/calls${window.location.search}`)}>‹ All calls</button>
            <CallView callId={callId} search={dq} onFiled={() => setReloadKey((k) => k + 1)} />
          </div>
        </>
      );
    }
    return (
      <>
        {top}
        <div className="cl-phone">{listPane}</div>
      </>
    );
  }

  return (
    <>
      {top}
      <div className="cl-wrap">
        {listPane}
        <section className="cl-pane" aria-label="Call">
          <CallView callId={callId} search={dq} onFiled={() => setReloadKey((k) => k + 1)} />
        </section>
      </div>
    </>
  );
}

function CallRow({ c, on, terms, onOpen }) {
  const who = theirNames(c.attendees);
  return (
    <button type="button" className={`cl-row${on ? " cl-row--on" : ""}`} onClick={onOpen} aria-current={on ? "true" : undefined}>
      <span className="cl-row__line1">
        <span className="cl-row__date">{dayLabel(c.started_at)}</span>
        {c.duration_min ? <span>{minutesLabel(c.duration_min)}</span> : null}
        {who && <span className="cl-row__who">{who}</span>}
        <span className={`cl-row__co${c.company_name ? "" : " cl-row__co--none"}`}>
          {c.company_name || (c.match_reason === "internal" ? "Internal" : "Not filed")}
        </span>
      </span>
      <span className="cl-row__title"><Hl text={c.title} terms={terms} /></span>
      {c.summary ? (
        <span className="cl-row__summary" dir="auto"><Hl text={c.summary} terms={terms} /></span>
      ) : (
        <span className="cl-row__summary cl-row__summary--none">{c.notes_status && c.notes_status !== "processed" && c.notes_status !== "skipped" ? "Fireflies is still writing its notes" : "No notes from Fireflies"}</span>
      )}
    </button>
  );
}
